import { devBus } from '../debug/devBus';
import type { PointerInteraction } from '../interaction/pointer';
import type { StoryCard } from './storyCard';

// The guided first-run loop (DEMO view only — never created under ?dev=1). One
// module owns a `phase` and drives the narrative card by WATCHING the primitives
// that already exist (the generator latch, the view toggle, cassette clicks, the
// era morph) — it adds no new systems, only orchestration (story-flow.md).
//
// The arc is a linear story dressed as free exploration: the visitor is carried
// through the loop once by a few required actions that feel discovered, then the
// loop completes and a minimalist control chrome is unlocked as a reward.
//
//   ARRIVE  present · dark · outside   → (generator pull)        → GENERATOR
//   GENERATOR powered                  → (go inside)             → INSIDE
//   INSIDE  interior · room alive      → (first cassette)        → PAST
//   PAST    era=past                   → (last cassette of set)  → RETURN
//   RETURN  morph back to present      → (auto)                  → SANDBOX
//   SANDBOX reward chrome unlocked     → free play
//
// At every GATED phase a watchdog escalates guidance: if the visitor lingers past
// the phase's idle threshold, the narrative card appears with the next step, so
// they're never stuck ("mostly clear and guided"). Acting before then advances
// the phase and the card never shows.

export type StoryPhase = 'arrive' | 'generator' | 'inside' | 'past' | 'return' | 'sandbox';

// Idle thresholds (ms) before the guidance card escalates for each gated phase.
// Tuned by John live; named so retuning is an edit, not a hunt.
const T_DARK = 2600;      // ARRIVE: look around, then "start the generator"
const T_INSIDE = 3600;    // GENERATOR: "step inside"
const T_CASSETTE = 4200;  // INSIDE: "play a cassette"
const T_PAST_NUDGE = 6500; // PAST: a gentle "try another / sit in the chair"
const RETURN_HOLD = 2800; // RETURN: let the morph-to-present read before the reward

// Story-flow #7 — SECONDS (not ms): how long the room beds take to swell up from
// silence once the generator powers the shop, so "the room comes alive" is a felt
// beat rather than a snap. John tunes by ear.
const T_ROOM_FADE_SEC = 3.5;

// EOD default — the number of cassette plays that completes the set (the last one
// triggers the return). John's data later; a hardcoded handful is fine for now.
const DEFAULT_SET_SIZE = 3;

// VO-ready card copy (text now, audio later). John tweaks the wording live.
const CARD = {
  arrive: 'The shop is dark. Start the generator by the door.',
  generator: 'The generator hums to life.',
  inside: 'Pick up a cassette to play it.',
  past: 'Try another tape — or sit in the chair to listen.',
} as const;

export interface StoryDirectorDeps {
  card: StoryCard;
  pointer: PointerInteraction;
  /** Current camera view — polled to detect the GENERATOR→INSIDE step (no event). */
  getView: () => 'exterior' | 'interior';
  /** Era now (past/present), derived from the morph. */
  currentEra: () => 'past' | 'present';
  /** Morph the room (stateController.setTarget) — the time machine + the return. */
  setEra: (era: 'past' | 'present') => void;
  /** Fire a narration clip by id (soundscape.fireClipExternally), latched. */
  fireNarration: (clipId: string) => void;
  /** RETURN→SANDBOX: the loop is complete — reveal the reward chrome (P0.5). */
  onReward?: () => void;
  /** Cassette plays that complete the set (last one returns). Default 3. */
  cassetteSetSize?: number;
  /** Glow a hero (HoverHighlight.focusRoot), or null to clear — the INSIDE-phase
   *  guidance pulse (cassette shelf, then chair). Optional: absent = no pulse. */
  glowHero?: (heroId: string | null) => void;
  /** Item 3 — the INSIDE-phase "play a cassette" glow needs a tape that's actually
   *  SHOWING + clickable in the present right now, not a hardcoded id that may be
   *  off-era or non-interactive (the old hero_cassette_case glowed nothing, so the
   *  visitor never found the trigger into the past). Returns a hero id to glow, or
   *  null to skip the cassette pulse. */
  pickShowingCassette?: () => string | null;
  /** Story-flow #7 — swell the room beds up from silence over N seconds when the
   *  generator powers the shop (audio.fadeInRoom). Optional: absent = beds snap on. */
  roomFadeIn?: (seconds: number) => void;
  /** Chair → "sit and listen": tween the camera into the seat (goToBookmark('chair')),
   *  whose onArrive fires the why-sudanese narration. Raised as a button on a chair
   *  click — the chair was otherwise inert. Optional. */
  sitInChair?: () => void;
  /** Generator → "step inside": go inside (setView interior). Raised as a pill in
   *  the action slot when the generator powers the shop — it carries the entry on
   *  first launch (the top go-inside button is hidden then). Optional. */
  goInside?: () => void;
}

export interface StoryDirector {
  /** Per-frame tick from the render loop — drives the idle watchdog + the
   *  view-poll advance. dtMs = elapsed milliseconds since the last frame. */
  update(dtMs: number): void;
  readonly phase: StoryPhase;
  /** Item 4 — n-of-N cassette progress for the bottom-context UI: `played` =
   *  distinct cassettes clicked so far, `total` = the set size (N) that returns
   *  the room to present. Pollable (the bottom UI reads it write-on-change). */
  cassetteProgress(): { played: number; total: number };
  dispose(): void;
}

export function createStoryDirector(deps: StoryDirectorDeps): StoryDirector {
  const setSize = Math.max(1, deps.cassetteSetSize ?? DEFAULT_SET_SIZE);

  // ARRIVE is the PRESENT-day shop (story-flow.md): the cassettes are the time
  // machine *to* the past, and RETURN carries you forward again. The scene's
  // resting default is past, so the loop asserts present at the start — the
  // morph back on the first cassette is then a felt beat, not a no-op. (John's
  // call if he'd rather the demo rest in past; this owns the loop's era arc.)
  deps.setEra('present');

  let phase: StoryPhase = 'arrive';
  let phaseElapsed = 0;       // ms in the current phase (the watchdog clock)
  let cardShown = false;      // has this phase's guidance card been raised yet?
  // Item 4 — progress is the set of DISTINCT cassette heroIds clicked, not a raw
  // click count: re-clicking the same tape no longer advances (which had read as
  // "it's a certain tape?"). Its size is the `n` of `n/N`.
  const distinctCassettes = new Set<string>();
  let timeMachineEngaged = false; // the first cassette morphs to past ONCE
  let wallNarrationFired = false;  // the cassette WALL fires Annour's narration ONCE
  let returnTimer = 0;
  const unsubs: Array<() => void> = [];

  // P1 #6 — INSIDE-phase glow-pulse choreography (cassette shelf → chair, sequential).
  // setTimeout-based so it runs even when the render loop is paused; cancelled on
  // any phase transition (and dispose).
  const PULSE_ON_MS = 1000, PULSE_GAP_MS = 350, INSIDE_GLOW_DELAY = 700;
  let glowTimers: number[] = [];
  const cancelGlow = (): void => {
    for (const t of glowTimers) clearTimeout(t);
    glowTimers = [];
    deps.glowHero?.(null);
  };
  const pulseHero = (heroId: string, atMs: number): void => {
    glowTimers.push(window.setTimeout(() => deps.glowHero?.(heroId), atMs));
    glowTimers.push(window.setTimeout(() => deps.glowHero?.(null), atMs + PULSE_ON_MS));
  };

  // Chair → a "sit and listen" button. The chair was inert: its narration
  // (narr_why_sudanese) had no trigger. A click on the chair raises this button;
  // pressing it tweens the camera into the seat (deps.sitInChair → goToBookmark),
  // and the bookmark's onArrive fires the line. A button "comes up" rather than an
  // instant jump so sitting reads as a deliberate choice. Glass pill matching the
  // view toggle so the affordance grammar is one language.
  const sitBtn = document.createElement('button');
  sitBtn.type = 'button';
  sitBtn.textContent = 'sit and listen';
  // Item 1 — the contextual affordance sits in a defined ACTION SLOT *above* the
  // story card (card is bottom:24%): action on top, guidance below, one place the
  // eye learns. The get-out pill shares this slot (they never co-show). ⚑ John
  // tunes the exact offset per device.
  // Legibility (John, 2026-06-29: "larger fonts + brighter text, they're hard to
  // see"): bright text on darker glass + bigger/bolder, matching the demoChip
  // idiom — readable over any scene, unlike the old dark-on-faint-glass pill.
  sitBtn.style.cssText = [
    'position: fixed', 'bottom: 33%', 'left: 50%', 'transform: translate(-50%, 8px)',
    'padding: 13px 30px', 'background: rgba(20,26,38,0.68)', 'color: #f4f8ff',
    'border: 1px solid rgba(150,178,228,0.45)', 'border-radius: 14px',
    'font: 700 16px/1.2 system-ui, sans-serif', 'letter-spacing: 0.02em',
    'text-shadow: 0 1px 3px rgba(0,0,0,0.55)', 'cursor: pointer', 'user-select: none',
    'backdrop-filter: blur(6px)', '-webkit-backdrop-filter: blur(6px)',
    'box-shadow: 0 3px 14px rgba(0,0,0,0.34)',
    'z-index: 1180', 'opacity: 0', 'pointer-events: none',
    'transition: opacity 320ms ease, transform 320ms ease',
  ].join('; ');
  document.body.appendChild(sitBtn);
  let sitShown = false;
  const showSit = (): void => {
    if (sitShown) return;
    sitShown = true;
    sitBtn.style.opacity = '1';
    sitBtn.style.pointerEvents = 'auto';
    sitBtn.style.transform = 'translate(-50%, 0)';
  };
  const hideSit = (): void => {
    if (!sitShown) return;
    sitShown = false;
    sitBtn.style.opacity = '0';
    sitBtn.style.pointerEvents = 'none';
    sitBtn.style.transform = 'translate(-50%, 8px)';
  };
  sitBtn.addEventListener('click', () => { hideSit(); deps.sitInChair?.(); });

  // Generator → a "step inside" button in the SAME action slot as the sit pill.
  // The pull-start raises it; clicking it tweens you inside. On first launch the
  // top go-inside button is hidden (app.ts suppressEntry), so this pill carries
  // the entry — the action lives in one place, above the guidance card.
  const stepInBtn = document.createElement('button');
  stepInBtn.type = 'button';
  stepInBtn.textContent = 'step inside';
  stepInBtn.style.cssText = sitBtn.style.cssText; // identical action-slot glass pill (starts hidden)
  document.body.appendChild(stepInBtn);
  let stepInShown = false;
  const showStepIn = (): void => {
    if (stepInShown) return;
    stepInShown = true;
    stepInBtn.style.opacity = '1';
    stepInBtn.style.pointerEvents = 'auto';
    stepInBtn.style.transform = 'translate(-50%, 0)';
  };
  const hideStepIn = (): void => {
    if (!stepInShown) return;
    stepInShown = false;
    stepInBtn.style.opacity = '0';
    stepInBtn.style.pointerEvents = 'none';
    stepInBtn.style.transform = 'translate(-50%, 8px)';
  };
  stepInBtn.addEventListener('click', () => { hideStepIn(); deps.goInside?.(); });

  const enter = (next: StoryPhase): void => {
    cancelGlow(); // a transition clears any pending guidance glow
    hideSit();    // and any raised chair button
    hideStepIn(); // and the step-inside pill
    phase = next;
    phaseElapsed = 0;
    cardShown = false;
    deps.card.hide();
    // Story-flow #7 — the generator wakes the shop: the room beds swell up from
    // silence over T_ROOM_FADE_SEC rather than snapping on (the pull-start chuff
    // is sfx, so it still cracks in at full — only the steady room rises). The
    // entry action moves into the slot: raise the "step inside" pill.
    if (next === 'generator') { deps.roomFadeIn?.(T_ROOM_FADE_SEC); showStepIn(); }
    if (next === 'inside') {
      // The touchable beats glow in sequence so they read as discovered before the
      // card escalates: a SHOWING cassette first (item 3 — one that's actually
      // visible + clickable this era, not the off-era non-interactive case), then
      // the chair. A null pick (no visible cassette) just skips the cassette pulse.
      const cassetteId = deps.pickShowingCassette?.();
      if (cassetteId) pulseHero(cassetteId, INSIDE_GLOW_DELAY);
      pulseHero('hero_chair', INSIDE_GLOW_DELAY + PULSE_ON_MS + PULSE_GAP_MS);
    }
  };

  // ARRIVE → GENERATOR: the generator pull-start (power latch). Demo-only event.
  unsubs.push(devBus.on('power:on', () => {
    if (phase === 'arrive') enter('generator');
  }));

  // Cassette clicks ride the same pointer 'click' the demoChip + interaction
  // rules use (the rule plays the song via the music override; the director adds
  // era + narration + counting). Only meaningful once inside.
  unsubs.push(deps.pointer.on('click', (info) => {
    const id = info.heroId;
    // Chair → raise the "sit and listen" button. The chair is a sit affordance
    // for the WHOLE interior visit, not just the guided inside/past beats: once
    // the loop completes (return/sandbox, i.e. free-roaming) you must STILL be
    // able to sit back down — Turki: "difficult to sit back down after getting
    // up… when roaming." Only arrive/generator (you're still outside, the chair
    // isn't even clickable) are excluded.
    if (id === 'hero_chair') {
      if (phase !== 'arrive' && phase !== 'generator') showSit();
      return;
    }
    // Cassette WALL → Annour's "cassettes vs digital" narration. John (2026-06-29)
    // moved it OFF the first-cassette play, where it landed too early and overlapped
    // the music (Bentley + Turki both flagged it). The archive itself speaks: click
    // the wall of tapes and Annour comes in. Fires once, latched, only once inside.
    if (id && id.startsWith('hero_wall_cassettes')) {
      if (phase !== 'arrive' && phase !== 'generator' && !wallNarrationFired) {
        wallNarrationFired = true;
        deps.fireNarration('narr_cassettes_clip');
      }
      return;
    }
    if (!id || !id.startsWith('hero_cassette')) return;
    if (phase !== 'inside' && phase !== 'past') return; // ignore before inside / after the loop
    // Item 4 — count DISTINCT cassettes. A NEW tape advances the n/N; re-clicking
    // an already-heard one is a no-op for progress (it still plays its song via
    // the interaction rule — only the story progress is distinct-gated).
    const isNewCassette = !distinctCassettes.has(id);
    distinctCassettes.add(id);
    // The time machine: the FIRST distinct cassette carries the room to the past.
    // (Annour's narration used to fire here too — moved to the cassette WALL click,
    // John 2026-06-29.) Guarded so a fast second click mid-morph can't re-engage.
    if (!timeMachineEngaged) {
      timeMachineEngaged = true;
      deps.setEra('past');
    }
    if (phase === 'inside') enter('past');
    // All N distinct cassettes heard → the loop completes, the room returns to
    // present. Only a newly-distinct cassette can cross the threshold.
    if (isNewCassette && distinctCassettes.size >= setSize) enterReturn();
  }));

  function enterReturn(): void {
    if (phase === 'return' || phase === 'sandbox') return;
    enter('return');
    deps.setEra('present'); // morph back — now meaningful, having been to the past
    returnTimer = window.setTimeout(() => {
      enter('sandbox');
      deps.onReward?.(); // unlock the reward chrome (P0.5)
    }, RETURN_HOLD);
  }

  const update = (dtMs: number): void => {
    phaseElapsed += dtMs;
    // GENERATOR→INSIDE has no event — detect the interior landing by polling.
    if (phase === 'generator' && deps.getView() === 'interior') { enter('inside'); return; }
    if (cardShown) return;
    // Per-gate idle watchdog: escalate to the card once the threshold passes.
    if (phase === 'arrive' && phaseElapsed > T_DARK) { deps.card.show(CARD.arrive, { persist: true }); cardShown = true; }
    else if (phase === 'generator' && phaseElapsed > T_INSIDE) { deps.card.show(CARD.generator, { persist: true }); cardShown = true; }
    else if (phase === 'inside' && phaseElapsed > T_CASSETTE) { deps.card.show(CARD.inside, { persist: true }); cardShown = true; }
    else if (phase === 'past' && phaseElapsed > T_PAST_NUDGE) { deps.card.show(CARD.past); cardShown = true; } // gentle, auto-fades
  };

  return {
    update,
    get phase() { return phase; },
    cassetteProgress: () => ({ played: distinctCassettes.size, total: setSize }),
    dispose(): void {
      if (returnTimer) clearTimeout(returnTimer);
      cancelGlow();
      sitBtn.remove();
      stepInBtn.remove();
      for (const u of unsubs) u();
    },
  };
}
